import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Tab,
  Tabs,
} from "@mui/material";
import { FormProvider, UseFormReturn, useForm } from "react-hook-form";
import {
  RHFTextField,
  RHFTimePicker,
} from "../../../../../../../RHFComponents/RHFComponents";
import { pgmedo, pgmedtage } from "../../../../../../../../networking";
import { formatHour } from "../../../../../../../AppointmentResearch/AppointmentResearch";
import { AllertMessage } from "../MedintAgendaFormDetail";

type OrariFissiForm = {
  orariFissiForm: {
    orarioPrestazione: any;
    maxPrest: string;
    durataStand: string;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  action: "insert" | "edit" | "copy";
  initialData?: any;
  parentMethods: UseFormReturn<any>;
  selectedGiornataId: string | null;
  createdIdPgmedt?: string;
};

// Valori di default nel form
const buildDefault = (): OrariFissiForm => ({
  orariFissiForm: {
    orarioPrestazione: null,
    maxPrest: "0",
    durataStand: "0",
  },
});

//L'api vuole gli orari come ad esempio 1450, quindi questa funzione si occupa di togliere i :
export const stripColonFromHour = (value?: string | null): string => {
  if (!value) return "";

  const trimmed = value.trim();

  // formato hh
  if (/^\d{4}$/.test(trimmed)) return trimmed;

  // formato HHmm, vado a sostituire i : con ""
  if (/^\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed.replace(":", "");
  }

  // tolgo eventuali : ovunque
  return trimmed.replace(/:/g, "");
};

const OrariFissiModal = ({
  open,
  onClose,
  action,
  initialData,
  parentMethods,
  selectedGiornataId,
  createdIdPgmedt,
}: Props) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [pendingSubmit, setPendingSubmit] = useState<OrariFissiForm | null>(
    null,
  );

  // form separato del modale, per poterlo resettare quando si chiude senza resettare quello di medintAgendaFormDetail
  const modalMethods = useForm<OrariFissiForm>({
    defaultValues: buildDefault(),
    mode: "onChange",
  });

  // UseEffect per riempire il form con i campi iniziali se siamo in edit o copy
  useEffect(() => {
    if (!open) return;
    setError(null);

    if (action === "insert") {
      modalMethods.reset(buildDefault());
      return;
    }

    modalMethods.reset({
      orariFissiForm: {
        orarioPrestazione: formatHour(
          initialData?.ORE ?? initialData?.PGMEDO_ORE,
        ),
        maxPrest: String(
          initialData?.MAXESAMI ?? initialData?.PGMEDO_NMAX ?? "0",
        ),
        durataStand: String(
          initialData?.DURATASTD ?? initialData?.PGMEDO_DURATA_STD ?? "0",
        ),
      },
    });
  }, [open, action, initialData, modalMethods]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmSubmit = modalMethods.handleSubmit((values) => {
    setPendingSubmit(values);

    setSeverity("warning");
    setMessage(
      action === "insert"
        ? "Confermi l'inserimento della prestazione?"
        : action === "edit"
          ? "Confermi la modifica della prestazione?"
          : "Confermi la copia della prestazione?",
    );
    setShowAllert(true);
  });

  const refreshProgram = async () => {
    const idPgmedt =
      parentMethods.getValues("medIntNuovoProgramma.chiave") ||
      (createdIdPgmedt as string | undefined);
    if (!idPgmedt)
      throw new Error("Manca la chiave agenda (medIntNuovoProgramma.chiave).");

    const response = await pgmedtage({ crud: "R", codicePgmedt: "", idPgmedt });
    const data = response?.data?.dati?.[0];
    if (!data) return;

    // aggiorno solo le tipologie: dentro ci sono anche le giornate e gli orari,
    // quindi si aggiorna tutto a cascata
    parentMethods.setValue(
      "medIntNuovoProgramma.tipologie",
      data.tipoPrenotazione ?? [],
      {
        shouldDirty: true,
        shouldTouch: false,
        shouldValidate: false,
      },
    );
  };

  // Premo il tasto di conferma e chiamo pgmedo e, attraverso il crud, eseguo inserimento, aggiornamento o duplicazione
  const submitHours = async () => {
    if (!pendingSubmit) return;
    setError(null);
    setLoading(true);

    try {
      const idPgmedt =
        parentMethods.getValues("medIntNuovoProgramma.chiave") ||
        (createdIdPgmedt as string | undefined);
      if (!idPgmedt)
        throw new Error("Impossibile salvare: manca l'ID agenda (chiave).");

      if (action === "insert" && !selectedGiornataId) {
        throw new Error(
          "Seleziona prima una giornata (serve per inserire un orario fisso).",
        );
      }

      const formValues = pendingSubmit.orariFissiForm;

      // crud backend: c/u/p (come gli altri modali)
      const crud = action === "insert" ? "c" : action === "edit" ? "u" : "p";

      console.log({ formValues });
      console.log({ initialData });
      console.log({ selectedGiornataId });

      const response = await pgmedo({
        crud,
        prgint: initialData?.IDPGMEDO ?? "",
        ...(action === "insert" || action === "edit"
          ? {
              dati: {
                pgmedo: {
                  ...(action === "insert"
                    ? { PGMEDG_PRGINT: String(selectedGiornataId) }
                    : {}),

                  ...(action === "edit"
                    ? {
                        PGMEDG_PRGINT: String(selectedGiornataId ?? ""),
                      }
                    : {}),

                  PGMEDO_ORE: stripColonFromHour(formValues.orarioPrestazione),
                  PGMEDO_NMAX: String(formValues.maxPrest ?? "0"),
                  PGMEDG_DURATA_STD: String(formValues.durataStand ?? "0"),
                },
              },
            }
          : {
              orario_fisso: stripColonFromHour(formValues.orarioPrestazione),
            }),
      });

      if (response?.data?.esito === "OK") {
        setLoading(false);
        setSeverity("success");
        setMessage("Operazione completata con successo.");
      } else {
        setSeverity("error");
        setMessage("Salvataggio non riuscito.");
        return;
      }

      await refreshProgram();
      setTimeout(() => onClose(), 800);
    } catch (e: any) {
      console.error("Errore salvataggio prestazione:", e);
      setSeverity("error");
      setMessage(e?.message ?? "Errore durante il salvataggio.");
    } finally {
      setPendingSubmit(null);
      setLoading(false);
    }
  };

  return (
    <>
      <AllertMessage
        dataToSend={pendingSubmit}
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={severity === "warning" ? submitHours : null}
      />
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <Modal
        open={open}
        onClose={(_, reason) => {
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          onClose();
        }}
      >
        <Box
          sx={{
            maxHeight: "90vh",
            maxWidth: "150vh",
            width: "50%",
            overflow: "auto",
            paddingTop: 1,
            backgroundColor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <FormProvider {...modalMethods}>
            <Tabs value={0} sx={{ paddingLeft: 2 }}>
              <Tab
                sx={{
                  fontFamily: "Montserrat",
                  fontWeight: "bold",
                  textTransform: "none",
                }}
                label="Orari fissi nella giornata - Dettaglio"
              />
            </Tabs>

            <Grid container spacing={2} padding={2}>
              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <RHFTimePicker
                  control={modalMethods.control}
                  name="orariFissiForm.orarioPrestazione"
                  label="Orario Prestazione"
                  size="small"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <RHFTextField
                  control={modalMethods.control}
                  name="orariFissiForm.maxPrest"
                  label="Numero massimo prestazioni"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <RHFTextField
                  control={modalMethods.control}
                  name="orariFissiForm.durataStand"
                  label="Durata standard"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container justifyContent="space-between">
                  <Button
                    variant="outlinedCustom"
                    onClick={onClose}
                    disabled={loading}
                  >
                    Indietro
                  </Button>

                  <Button
                    variant="containedCustom"
                    disabled={loading}
                    onClick={askConfirmSubmit}
                  >
                    Conferma
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </FormProvider>
        </Box>
      </Modal>
    </>
  );
};

export default OrariFissiModal;
